/**
 * EVENT DETAIL.
 *
 * Presentation refresh onto the shared kit, plus the one structural change
 * the events/venues split demands: **when an event is attached to a real
 * venue, say so and link to it.**
 *
 * Before, `data.venue` was rendered as a bare string next to a 🏛️ emoji even
 * when `venueId` pointed at a live `bar_listings` row, so the journey
 * "this gig → which pub is that? → what else is on there? → has it got a beer
 * garden?" simply did not exist on the web. That journey is the entire point
 * of separating the two entities.
 *
 * DELIBERATELY UNCHANGED — all of this is load-bearing and hard-won:
 *   • `useGetEventById` (resolves by numeric id OR slug, so all 22 indexed
 *     `/events/<slug>` URLs keep working)
 *   • the saved-state hydration round trip and its optimistic rollback
 *   • the .ics "Add to calendar" export
 *   • `useSEO` including the Schema.org `Event` JSON-LD — this is the only
 *     structured data the site emits for events, and it feeds Google's event
 *     rich results
 *   • ShareBar and the GoogleAd slot
 */
import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "wouter";
import { useGetEventById } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { useSEO } from "@/hooks/use-seo";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ShareBar } from "@/components/ShareBar";
import { GoogleAd } from "@/components/GoogleAd";
import { QueryState, isNotFoundError } from "@/components/QueryState";
import { cn } from "@/lib/utils";
import {
  CalendarPlus, ChevronLeft, ChevronRight, Clock, Heart, MapPin, Ticket, User,
} from "lucide-react";
import {
  Chip, DateTile, ELEVATION, FOCUS_RING, RADII, TYPE,
  categoryTone, formatTime, groupLabel, venueTypeLabel, useVenue,
} from "@/components/hub";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

/**
 * `GET /events/:id` returns the whole row, but the generated `Event` type is
 * built from an openapi.yaml that predates half the columns (audit §4.6).
 * Only the extra fields this page reads are declared.
 */
type EventExtras = {
  venueId?: number | null;
  isVenueEvent?: boolean | null;
  endTime?: string | null;
  shortSummary?: string | null;
  ticketUrl?: string | null;
  ageRestriction?: string | null;
  accessibilityInfo?: string | null;
};

export default function EventDetail() {
  // The route may be `/events/:segment` (via EventsRoute) or `/event/:id`.
  const params = useParams<{ id?: string; segment?: string }>();
  const id = params.id ?? params.segment ?? "";
  const { data: rawData, isLoading, isError, error, refetch } = useGetEventById(id);
  const data = rawData as (typeof rawData & EventExtras) | undefined;
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [saved, setSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  /* Only fetch the venue when the event actually points at one. 11 of 19
     audited events have `venue_id IS NULL` — for those the location stays
     free text and there is nothing to link to, which is correct, not a gap. */
  const venueQuery = useVenue(data?.venueId ? String(data.venueId) : undefined);
  const venue = venueQuery.data;

  // Hydrate the heart from the server — otherwise an already-saved event always
  // renders as unsaved and the first tap fires a POST for a row that exists.
  useEffect(() => {
    if (!user || !id) { setSaved(false); return; }
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await fetch("/api/saved?itemType=event", { headers: authHeaders(), signal: controller.signal });
        if (!res.ok) return;
        const body = await res.json() as { items?: { itemId: number }[] };
        setSaved((body.items ?? []).some((i) => Number(i.itemId) === Number(data?.id ?? id)));
      } catch { /* offline or aborted — leave the button in its default state */ }
    })();
    return () => controller.abort();
  }, [user, id, data?.id]);

  const handleAddToCalendar = () => {
    if (!data) return;
    const dateStr = (data.eventDate ?? "").replace(/-/g, "");
    if (!dateStr) return;
    const location = [data.address, data.venue, data.town].filter(Boolean).join(", ");
    const desc = (data.description ?? "").replace(/\n/g, "\\n").replace(/,/g, "\\,");
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Ayrshire Hub//Events//EN",
      "BEGIN:VEVENT",
      `UID:${data.id}@ayrshirehub.co.uk`,
      `DTSTART;VALUE=DATE:${dateStr}`,
      `DTEND;VALUE=DATE:${dateStr}`,
      `SUMMARY:${data.name}`,
      `DESCRIPTION:${desc}`,
      `LOCATION:${location}`,
      `URL:https://www.ayrshirehub.co.uk/events/${data.id}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    if (!data) return;
    if (!user) {
      navigate(`/login?returnTo=${encodeURIComponent(`/event/${data.id}`)}`);
      return;
    }
    const next = !saved;
    setSaveLoading(true);
    setSaved(next); // optimistic
    try {
      const res = await fetch(`/api/events/${data.id}/save`, {
        method: next ? "POST" : "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
    } catch {
      setSaved(!next); // roll back — the server never recorded the change
      toast({
        title: next ? "Couldn't save this event" : "Couldn't remove this event",
        description: "Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  useSEO({
    title: data?.name ?? undefined,
    description: data ? [data.description, data.town].filter(Boolean).join(" · ") : undefined,
    image: data?.imageUrl ?? undefined,
    type: "article",
    jsonLd: data ? {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": data.name,
      "startDate": data.eventDate,
      "url": `https://www.ayrshirehub.co.uk/events/${data.id}`,
      ...(data.description ? { "description": data.description } : {}),
      ...(data.imageUrl ? { "image": data.imageUrl } : {}),
      ...(data.organiserName ? { "organizer": { "@type": "Organization", "name": data.organiserName } } : {}),
      "location": {
        "@type": "Place",
        "name": data.venue || data.town,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": data.town,
          "addressRegion": "Ayrshire",
          "addressCountry": "GB",
        },
      },
    } : undefined,
  });

  // A 404 means the event genuinely isn't there — retrying will never help, so
  // fall through to the "not found" card below. Any other failure is a
  // transport/server problem and the user gets a retry.
  const isMissing = !isLoading && (isNotFoundError(error) || (!isError && !data));

  if (isLoading || (isError && !isMissing)) {
    return (
      <QueryState
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        skeleton={
          <div className="container mx-auto px-4 py-8 max-w-3xl">
            <div className={cn(RADII.md, "h-56 sm:h-72 skeleton-shimmer bg-slate-200/70 mb-6")} />
            <div className="space-y-3">
              <div className="h-7 w-3/4 rounded skeleton-shimmer bg-slate-200/70" />
              <div className="h-4 w-1/2 rounded skeleton-shimmer bg-slate-200/70" />
            </div>
          </div>
        }
        errorTitle="Couldn't load this event"
        errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
        errorAction={{ label: "Back to What's On", href: "/events" }}
        fullPage
      />
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-5xl mb-4" aria-hidden>🎉</p>
        <p className={cn(TYPE.title2, "text-slate-900 mb-1")}>Event not found</p>
        <p className={cn(TYPE.callout, "text-slate-500 mb-6")}>
          It may have been cancelled or removed.
        </p>
        <Link href="/events"><Button>Back to What's On</Button></Link>
      </div>
    );
  }

  const price = (data.price ?? "").trim();
  const isFree = price === "" || price === "0" || price.toLowerCase() === "free";
  const dateLabel = data.eventDate ? groupLabel(data.eventDate) : "";
  const timeLabel = formatTime(data.eventTime);
  const bookingHref = data.ticketUrl ?? data.bookingLink ?? null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-4 sm:py-6 max-w-3xl">
        <nav aria-label="Breadcrumb" className={cn(TYPE.footnote, "flex items-center gap-1.5 text-slate-400 mb-3")}>
          <Link href="/events" className={cn(FOCUS_RING, "inline-flex items-center gap-1 hover:text-[#1e3a5f] transition-colors")}>
            <ChevronLeft size={14} aria-hidden /> What's On
          </Link>
          <span aria-hidden>/</span>
          <span className="text-slate-500 truncate" aria-current="page">{data.name}</span>
        </nav>

        <article className={cn(RADII.md, ELEVATION.sm, "bg-white border border-slate-200/80 overflow-hidden")}>
          {data.imageUrl && (
            <img src={data.imageUrl} alt="" className="w-full aspect-[21/9] sm:aspect-[16/7] object-cover" />
          )}

          <div className="p-4 sm:p-6 space-y-6">
            {/* ── Headline ─────────────────────────────────────────────── */}
            <div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {data.featured && <Chip tone="gold" size="sm">Featured</Chip>}
                {data.category && (
                  <Chip tone={categoryTone(data.category)} size="sm">{data.category}</Chip>
                )}
              </div>
              <h1 className={cn(TYPE.display, "text-slate-900")}>{data.name}</h1>
              {data.shortSummary && (
                <p className={cn(TYPE.body, "text-slate-500 mt-2")}>{data.shortSummary}</p>
              )}
            </div>

            {/* ── When ─────────────────────────────────────────────────── */}
            <div className="flex items-center gap-3">
              {data.eventDate && <DateTile date={data.eventDate} />}
              <div className="min-w-0">
                <p className={cn(TYPE.title3, "text-slate-900")}>{dateLabel}</p>
                {/* `event_time` is unparseable free text on 11 of 19 rows
                    ("DOORS 1pm SHOW 2pm"). formatTime tidies a clean HH:MM and
                    otherwise prints the author's own words rather than losing
                    them (audit R6). */}
                {data.eventTime && (
                  <p className={cn(TYPE.callout, "text-slate-500 inline-flex items-center gap-1.5 mt-0.5")}>
                    <Clock size={13} aria-hidden />
                    {timeLabel}
                    {data.endTime ? ` – ${formatTime(data.endTime)}` : ""}
                  </p>
                )}
              </div>
            </div>

            {/* ── Where ────────────────────────────────────────────────────
                THE SPLIT, made visible. A `venue_id` means a real place with
                its own page, its own facilities and its own other events, so
                the whole block becomes a link. Without one the location is
                honest free text — a park, a hall, a street address — and it is
                NOT dressed up as something clickable. */}
            {venue ? (
              <Link
                href={venue.slug ? `/venue/${venue.slug}` : `/venues/${venue.id}`}
                className={cn(
                  RADII.md, FOCUS_RING,
                  "group flex items-center gap-3 border border-slate-200 bg-slate-50 p-3 transition-colors hover:border-slate-300 hover:bg-white",
                )}
              >
                <div className={cn(RADII.sm, "w-11 h-11 shrink-0 grid place-items-center bg-gradient-to-br from-[#0c1e33] to-[#1e3a5f] overflow-hidden")}>
                  {venue.coverPhoto
                    ? <img src={venue.coverPhoto} alt="" className="w-full h-full object-cover" />
                    : <span className="text-white/50 text-lg" aria-hidden>🍺</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn(TYPE.headline, "text-slate-900 line-clamp-1")}>{venue.name}</p>
                  <p className={cn(TYPE.footnote, "text-slate-500 line-clamp-1")}>
                    {[venueTypeLabel(venue.venueType), venue.address, venue.town].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <span className={cn(TYPE.caption, "hidden sm:inline text-[#1e3a5f] shrink-0")}>See the venue</span>
                <ChevronRight size={16} className="text-slate-400 shrink-0" aria-hidden />
              </Link>
            ) : (
              (data.venue?.trim() || data.address || data.town) && (
                <div className={cn(RADII.md, "flex items-start gap-3 border border-slate-200 bg-slate-50 p-3")}>
                  <MapPin size={16} className="text-slate-400 mt-0.5 shrink-0" aria-hidden />
                  <div className="min-w-0">
                    {data.venue?.trim() && (
                      <p className={cn(TYPE.bodyStrong, "text-slate-800")}>{data.venue.trim()}</p>
                    )}
                    <p className={cn(TYPE.callout, "text-slate-500")}>
                      {[data.address, data.town].filter(Boolean).join(", ")}
                    </p>
                  </div>
                </div>
              )
            )}

            {/* ── Facts ────────────────────────────────────────────────── */}
            <ul className={cn(TYPE.callout, "grid gap-2 sm:grid-cols-2 text-slate-600")}>
              <li className="inline-flex items-center gap-2">
                <Ticket size={15} className="text-slate-400 shrink-0" aria-hidden />
                <span className={isFree ? "font-semibold text-emerald-700" : "font-semibold text-slate-800"}>
                  {isFree ? "Free entry" : price}
                </span>
              </li>
              {data.organiserName && (
                <li className="inline-flex items-center gap-2 min-w-0">
                  <User size={15} className="text-slate-400 shrink-0" aria-hidden />
                  <span className="truncate">Organised by {data.organiserName}</span>
                </li>
              )}
              {data.ageRestriction && (
                <li className="inline-flex items-center gap-2">
                  <span className="text-slate-400" aria-hidden>🔞</span>
                  <span>{data.ageRestriction}</span>
                </li>
              )}
              {data.accessibilityInfo && (
                <li className="inline-flex items-center gap-2">
                  <span className="text-slate-400" aria-hidden>♿</span>
                  <span>{data.accessibilityInfo}</span>
                </li>
              )}
            </ul>

            {data.description && (
              <div>
                <h2 className={cn(TYPE.title3, "text-slate-900 mb-2")}>About this event</h2>
                <p className={cn(TYPE.body, "text-slate-600 whitespace-pre-wrap")}>{data.description}</p>
              </div>
            )}

            <GoogleAd />

            {bookingHref && (
              <a href={bookingHref} target="_blank" rel="noreferrer" className="block">
                <Button className="w-full h-11 bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
                  <Ticket size={15} className="mr-1.5" aria-hidden /> Book tickets
                </Button>
              </a>
            )}

            {/* ── Personal actions ─────────────────────────────────────── */}
            <div className="pt-2 border-t border-slate-100 space-y-2.5">
              <div className="grid gap-2.5 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saveLoading}
                  aria-pressed={saved}
                  className={cn(
                    RADII.sm, TYPE.callout, FOCUS_RING,
                    "w-full h-11 inline-flex items-center justify-center gap-2 border font-semibold transition-colors disabled:opacity-50 cursor-pointer",
                    saved
                      ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900",
                  )}
                >
                  <Heart size={15} className={saved ? "fill-red-500 text-red-500" : ""} aria-hidden />
                  {!user ? "Log in to save" : saved ? "Saved" : "Save event"}
                </button>

                {data.eventDate && (
                  <button
                    type="button"
                    onClick={handleAddToCalendar}
                    className={cn(
                      RADII.sm, TYPE.callout, FOCUS_RING,
                      "w-full h-11 inline-flex items-center justify-center gap-2 border border-slate-200 bg-white font-semibold text-slate-600",
                      "hover:border-slate-300 hover:text-slate-900 transition-colors cursor-pointer",
                    )}
                  >
                    <CalendarPlus size={15} aria-hidden />
                    Add to calendar
                  </button>
                )}
              </div>

              <ShareBar
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={data.name}
              />
            </div>
          </div>
        </article>

        {/* Back into the feed with the event's own context pre-applied. */}
        <div className="mt-5 flex flex-wrap gap-2">
          <Link
            href={`/events/${(data.town ?? "").toLowerCase().replace(/\s+/g, "-")}`}
            className={cn(
              TYPE.caption, RADII.pill, FOCUS_RING,
              "inline-flex items-center gap-1.5 h-9 px-3.5 bg-white border border-slate-200 text-slate-600",
              "hover:border-slate-300 hover:text-slate-900 transition-colors",
            )}
          >
            More in {data.town}
          </Link>
          <Link
            href="/events/this-weekend"
            className={cn(
              TYPE.caption, RADII.pill, FOCUS_RING,
              "inline-flex items-center gap-1.5 h-9 px-3.5 bg-white border border-slate-200 text-slate-600",
              "hover:border-slate-300 hover:text-slate-900 transition-colors",
            )}
          >
            What's on this weekend
          </Link>
          <Link
            href="/places"
            className={cn(
              TYPE.caption, RADII.pill, FOCUS_RING,
              "inline-flex items-center gap-1.5 h-9 px-3.5 bg-white border border-slate-200 text-slate-600",
              "hover:border-slate-300 hover:text-slate-900 transition-colors",
            )}
          >
            Browse places
          </Link>
        </div>
      </div>
    </div>
  );
}
